/**
 * Phase 5.2c-pre — Active TikTok account context.
 *
 * One source of truth for "which connected TikTok account is the user
 * currently working as". Library / Generate / Marketplace read it via
 * `useActiveAccount()` and filter their content per account.
 *
 * State:
 *   - accounts: array of connected TikTok accounts from /api/tiktok/accounts
 *   - activeAccount: the currently-selected account (or null)
 *   - environment: "sandbox" | "production" — used for the sandbox banner
 *
 * Persistence: `localStorage["active_tiktok_account_id"]`. We don't store
 * the whole object — only the id — so it survives token refreshes and
 * stays in sync with the latest server-side metadata.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";

const LS_KEY = "active_tiktok_account_id";
const ActiveAccountCtx = createContext(null);

export function ActiveAccountProvider({ children }) {
  const [accounts, setAccounts] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [activeId, setActiveId] = useState(
    () => (typeof window !== "undefined" && window.localStorage.getItem(LS_KEY)) || null,
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tiktok/accounts`);
      const list = r.data?.accounts || [];
      setAccounts(list);
      setEnvironment(r.data?.environment || null);
      // If the persisted id no longer exists in the accounts list (account
      // was revoked), drop it so we don't filter on a dead reference.
      setActiveId((prev) => {
        if (prev && list.find((a) => a.id === prev)) return prev;
        // Default to the first account if any.
        const first = list[0]?.id || null;
        if (first) window.localStorage.setItem(LS_KEY, first);
        else window.localStorage.removeItem(LS_KEY);
        return first;
      });
    } catch (_) {
      setAccounts([]);
      setEnvironment(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setActiveAccount = useCallback((id) => {
    if (id) window.localStorage.setItem(LS_KEY, id);
    else window.localStorage.removeItem(LS_KEY);
    setActiveId(id);
  }, []);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.id === activeId) || null,
    [accounts, activeId],
  );

  const value = useMemo(
    () => ({ accounts, activeAccount, setActiveAccount, environment, loading, reload: load }),
    [accounts, activeAccount, setActiveAccount, environment, loading, load],
  );

  return <ActiveAccountCtx.Provider value={value}>{children}</ActiveAccountCtx.Provider>;
}

export function useActiveAccount() {
  const ctx = useContext(ActiveAccountCtx);
  if (!ctx) throw new Error("useActiveAccount must be used inside <ActiveAccountProvider>");
  return ctx;
}
